import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminUsers, creditPoints, blockUser, unblockUser, deleteUser, adminGetUserVisitHistory, adminSendMagicLink, adminGetUserEntries, adminResendTicketsEmail } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Coins, Search, Eye, User, Mail, Phone, MapPin, Calendar, Crown, Ticket, Wallet, Hash, Ban, ShieldCheck, Trash2, AlertTriangle, Activity, Monitor, Smartphone, Tablet, ExternalLink, Globe, Clock, Loader2, Send } from 'lucide-react';

const vipLabels = ['Member', 'Bronze', 'Silver', 'Gold'];
const vipColors = ['bg-muted text-muted-foreground', 'bg-amber-100 text-amber-700', 'bg-gray-200 text-gray-700', 'bg-yellow-100 text-yellow-700'];

export default function AdminUsers() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, user: null, typed: '' });
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditForm, setCreditForm] = useState({ amount: '', reason: '', payment_method: 'cash', amount_gbp: '' });
  const [visitsDialogOpen, setVisitsDialogOpen] = useState(false);
  const [visitsData, setVisitsData] = useState(null);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [userEntries, setUserEntries] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [resendingRaffle, setResendingRaffle] = useState(null);

  useEffect(() => { if (authLoading) return; if (!user || !isAdmin) { navigate('/admin/login'); return; } loadUsers(); }, [user, isAdmin, navigate, authLoading]);
  useEffect(() => { const q = search.toLowerCase(); setFiltered(users.filter(u => u.email?.toLowerCase().includes(q) || u.first_name?.toLowerCase().includes(q) || u.last_name?.toLowerCase().includes(q))); }, [search, users]);

  const loadUsers = async () => { try { const res = await getAdminUsers(); setUsers(res.data.users || []); setFiltered(res.data.users || []); } catch (e) { console.error(e); } finally { setLoading(false); } };

  const openCredit = (u) => { setSelectedUser(u); setCreditForm({ amount: '', reason: '', payment_method: 'cash', amount_gbp: '' }); setCreditDialogOpen(true); };
  const openProfile = async (u) => {
    setSelectedUser(u);
    setProfileDialogOpen(true);
    setUserEntries(null);
    setEntriesLoading(true);
    try { const res = await adminGetUserEntries(u._id); setUserEntries(res.data); }
    catch { setUserEntries({ raffles: [], total_tickets: 0 }); }
    finally { setEntriesLoading(false); }
  };
  const handleResendTickets = async (raffleId) => {
    setResendingRaffle(raffleId);
    try {
      const res = await adminResendTicketsEmail(selectedUser._id, raffleId);
      toast.success(`Tickets renvoyés à ${res.data.email} (${res.data.tickets} ticket·s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\'envoi');
    } finally { setResendingRaffle(null); }
  };
  const openVisits = async (u) => {
    setSelectedUser(u);
    setVisitsDialogOpen(true);
    setVisitsLoading(true);
    setVisitsData(null);
    try {
      const res = await adminGetUserVisitHistory(u._id);
      setVisitsData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setVisitsLoading(false);
    }
  };

  const handleCredit = async () => {
    const amount = parseInt(creditForm.amount);
    if (!amount || isNaN(amount)) { toast.error(t('enterValidAmount') || 'Enter a valid amount'); return; }
    const payload = {
      user_id: selectedUser._id,
      amount,
      reason: creditForm.reason || 'Admin credit',
      payment_method: creditForm.payment_method || 'offert',
    };
    // amount_gbp defaults to amount if empty
    if (creditForm.amount_gbp !== '' && !isNaN(parseFloat(creditForm.amount_gbp))) {
      payload.amount_gbp = parseFloat(creditForm.amount_gbp);
    }
    try {
      const res = await creditPoints(payload);
      const counted = res.data?.counted_in_sales;
      toast.success(
        `${amount > 0 ? 'Crédité' : 'Débité'} ${Math.abs(amount)} pts` +
        (amount > 0 ? (counted ? ' · comptabilisé dans les ventes' : ' · OFFERT (non comptabilisé)') : '')
      );
      setCreditDialogOpen(false);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const openConfirm = (action, u) => setConfirmDialog({ open: true, action, user: u, typed: '' });

  const handleSendMagicLink = async (u) => {
    try {
      await adminSendMagicLink(u._id);
      toast.success(`Email d'accès envoyé à ${u.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de l\'envoi');
    }
  };

  const performAction = async () => {
    if (!confirmDialog.user || !confirmDialog.action) return;
    const { action, user: u } = confirmDialog;
    if (action === 'delete' && confirmDialog.typed !== 'DELETE') {
      toast.error(t('typeDeleteToConfirm') || 'Type DELETE to confirm');
      return;
    }
    setActionLoading(true);
    try {
      if (action === 'block') {
        await blockUser(u._id);
        toast.success(t('userBlocked') || 'User blocked');
      } else if (action === 'unblock') {
        await unblockUser(u._id);
        toast.success(t('userUnblocked') || 'User unblocked');
      } else if (action === 'delete') {
        await deleteUser(u._id);
        toast.success(t('userDeleted') || 'User deleted');
      }
      setConfirmDialog({ open: false, action: null, user: null, typed: '' });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  if (authLoading || !user || !isAdmin) return null;

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="mb-8"><h1 className="font-display text-3xl font-bold tracking-tight">{t('users')}</h1><p className="text-muted-foreground mt-1 text-sm">{t('manageUsersDesc')}</p></div>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-sm font-semibold text-rolex-green">{users.length} {t('users')}</p>
          <div className="relative max-w-sm flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder={t('searchUsers')} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-xl" data-testid="users-search-input" /></div>
        </div>
        <Card className="bg-white border-border" data-testid="admin-users-table"><CardContent className="p-0">
          <Table><TableHeader><TableRow><TableHead>{t('name')}</TableHead><TableHead>{t('email')}</TableHead><TableHead>{t('role')}</TableHead><TableHead>{t('status') || 'Status'}</TableHead><TableHead>{t('points')}</TableHead><TableHead>{t('entries')}</TableHead><TableHead>{t('joined')}</TableHead><TableHead className="text-right">{t('actions')}</TableHead></TableRow></TableHeader>
            <TableBody>{filtered.map((u) => (
              <TableRow key={u._id} className={u.blocked ? 'opacity-60 bg-red-50/40' : ''}>
                <TableCell className="font-medium cursor-pointer hover:text-primary" onClick={() => openProfile(u)}>{u.first_name} {u.last_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className={u.role === 'admin' ? 'bg-primary/10 text-primary border-primary/20' : ''}>{u.role}</Badge></TableCell>
                <TableCell>
                  {u.blocked ? (
                    <Badge className="bg-red-100 text-red-700 border-red-300 gap-1 text-[10px]"><Ban className="w-3 h-3" /> {t('blocked') || 'Blocked'}</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-700 border-green-300 gap-1 text-[10px]"><ShieldCheck className="w-3 h-3" /> {t('active') || 'Active'}</Badge>
                  )}
                </TableCell>
                <TableCell className="font-mono font-semibold text-primary">{u.points_balance || 0}</TableCell>
                <TableCell className="font-mono">{u.total_entries || 0}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end flex-wrap">
                    <Button size="sm" variant="ghost" className="gap-1 rounded-lg" onClick={() => openProfile(u)} title={t('view') || 'View'}><Eye className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="gap-1 rounded-lg border-emerald-400 !text-emerald-700 hover:bg-emerald-50" onClick={() => openVisits(u)} title="Historique des visites" data-testid={`visits-${u._id}`}>
                      <Activity className="w-3.5 h-3.5" />
                    </Button>
                    {u.role !== 'admin' && (
                      <>
                        <Button size="sm" variant="outline" className="gap-1 rounded-lg" onClick={() => openCredit(u)} title={t('creditDebitPoints')}>
                          <Coins className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1 rounded-lg border-amber-400 !text-amber-700 hover:bg-amber-50" onClick={() => handleSendMagicLink(u)} title="Envoyer le lien d'accès (1 clic)" data-testid={`magic-link-${u._id}`}>
                          <Send className="w-3.5 h-3.5" />
                        </Button>
                        {u.blocked ? (
                          <Button size="sm" variant="outline" className="gap-1 rounded-lg border-green-400 !text-green-700 hover:bg-green-50" onClick={() => openConfirm('unblock', u)} title={t('unblock') || 'Unblock'} data-testid={`unblock-${u._id}`}>
                            <ShieldCheck className="w-3.5 h-3.5" />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="gap-1 rounded-lg border-orange-400 !text-orange-700 hover:bg-orange-50" onClick={() => openConfirm('block', u)} title={t('block') || 'Block'} data-testid={`block-${u._id}`}>
                            <Ban className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="gap-1 rounded-lg border-red-400 !text-red-700 hover:bg-red-50" onClick={() => openConfirm('delete', u)} title={t('delete') || 'Delete'} data-testid={`delete-${u._id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </CardContent></Card>

        {/* Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="bg-white border-border sm:max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-xl">{t('userProfile')}</DialogTitle></DialogHeader>
            {selectedUser && (
              <div className="space-y-5 py-2">
                {/* Header */}
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center shrink-0">
                    <User className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-bold">{selectedUser.first_name} {selectedUser.last_name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                    <Badge className={`mt-1 ${vipColors[selectedUser.vip_level || 0]} border-none gap-1 text-xs`}><Crown className="w-3 h-3" /> VIP: {vipLabels[selectedUser.vip_level || 0]}</Badge>
                  </div>
                </div>

                <Separator />

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-accent/50 rounded-xl p-3 text-center">
                    <Wallet className="w-4 h-4 text-primary mx-auto mb-1" />
                    <p className="font-mono font-bold text-primary text-lg">{selectedUser.points_balance || 0}</p>
                    <p className="text-[10px] text-muted-foreground">{t('points')}</p>
                  </div>
                  <div className="bg-accent/50 rounded-xl p-3 text-center">
                    <Ticket className="w-4 h-4 text-primary mx-auto mb-1" />
                    <p className="font-mono font-bold text-lg">{selectedUser.total_entries || 0}</p>
                    <p className="text-[10px] text-muted-foreground">{t('entries')}</p>
                  </div>
                  <div className="bg-accent/50 rounded-xl p-3 text-center">
                    <Coins className="w-4 h-4 text-primary mx-auto mb-1" />
                    <p className="font-mono font-bold text-lg">{selectedUser.total_spent || 0}</p>
                    <p className="text-[10px] text-muted-foreground">{t('totalSpent')}</p>
                  </div>
                </div>

                <Separator />

                {/* Tickets par tirage */}
                <div className="space-y-2" data-testid="user-tickets-by-raffle">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-foreground flex items-center gap-1.5"><Ticket className="w-4 h-4 text-primary" /> Tickets par tirage</h4>
                    {userEntries && <span className="text-xs text-muted-foreground">{userEntries.total_tickets} ticket·s au total</span>}
                  </div>
                  {entriesLoading ? (
                    <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gold" /></div>
                  ) : userEntries && userEntries.raffles.length > 0 ? (
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {userEntries.raffles.map((r) => (
                        <div key={r.raffle_id} className="rounded-xl border border-gold/20 bg-cream/60 p-3" data-testid={`user-raffle-${r.raffle_id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {r.raffle_image && <img src={r.raffle_image} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" />}
                              <div className="min-w-0">
                                <p className="font-semibold text-sm text-rolex-green truncate">{r.raffle_title}</p>
                                <p className="text-[11px] text-rolex-green/65"><strong className="text-primary">{r.quantity}</strong> ticket·s · £{Number(r.amount_paid_gbp).toFixed(2)}</p>
                              </div>
                            </div>
                            <Button size="sm" variant="outline" className="gap-1 rounded-lg border-blue-400 !text-blue-700 hover:bg-blue-50 shrink-0"
                              onClick={() => handleResendTickets(r.raffle_id)} disabled={resendingRaffle === r.raffle_id}
                              title="Renvoyer les tickets par email" data-testid={`resend-tickets-${r.raffle_id}`}>
                              {resendingRaffle === r.raffle_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                              <span className="text-[11px]">Renvoyer</span>
                            </Button>
                          </div>
                          {r.ticket_codes?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {r.ticket_codes.map((c, i) => (
                                <span key={i} className="font-mono text-[10px] bg-rolex-green text-gold px-1.5 py-0.5 rounded border border-gold/30">{c}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic py-2">Aucun ticket pour ce client.</p>
                  )}
                </div>

                <Separator />

                {/* Personal Info */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-foreground">{t('personalInfo')}</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="w-4 h-4 shrink-0" />
                      <span className="truncate">{selectedUser.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="w-4 h-4 shrink-0" />
                      <span>{selectedUser.phone || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="w-4 h-4 shrink-0" />
                      <span>{[selectedUser.address, selectedUser.city, selectedUser.postcode].filter(Boolean).join(', ') || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span>{selectedUser.date_of_birth || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="w-4 h-4 shrink-0" />
                      <span>{selectedUser.gender || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Hash className="w-4 h-4 shrink-0" />
                      <span className="font-mono text-xs">{selectedUser.referral_code || '-'}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Meta */}
                <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                  <div><span className="font-medium text-foreground">{t('joined')}:</span> {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString('fr-FR') : '-'}</div>
                  <div><span className="font-medium text-foreground">ID:</span> <span className="font-mono">{selectedUser._id}</span></div>
                  {selectedUser.referred_by && <div><span className="font-medium text-foreground">{t('referredBy')}:</span> <span className="font-mono">{selectedUser.referred_by}</span></div>}
                  {selectedUser.referral_count > 0 && <div><span className="font-medium text-foreground">{t('referrals')}:</span> {selectedUser.referral_count}</div>}
                </div>
              </div>
            )}
            <DialogFooter>
              {selectedUser && selectedUser.role !== 'admin' && (
                <Button variant="outline" className="gap-2 rounded-xl" onClick={() => { setProfileDialogOpen(false); openCredit(selectedUser); }}>
                  <Coins className="w-4 h-4" /> {t('creditDebitPoints')}
                </Button>
              )}
              <Button onClick={() => setProfileDialogOpen(false)} className="rounded-xl">{t('close')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Visit History Dialog */}
        <Dialog open={visitsDialogOpen} onOpenChange={setVisitsDialogOpen}>
          <DialogContent className="bg-white border-border max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-600" /> Historique des visites
              </DialogTitle>
            </DialogHeader>
            {visitsLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-gold" />
              </div>
            ) : visitsData ? (
              <div className="space-y-5 py-2">
                {/* Header */}
                <div className="flex items-center gap-3 p-3 rounded-xl bg-cream border border-gold/20">
                  <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center shrink-0">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-rolex-green">{visitsData.user.first_name} {visitsData.user.last_name}</p>
                    <p className="text-xs text-rolex-green/65">{visitsData.user.email}</p>
                  </div>
                  {visitsData.user.vip_status && (
                    <Badge className="bg-gold-gradient text-rolex-green border-none gap-1"><Crown className="w-3 h-3" /> VIP</Badge>
                  )}
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <MiniStat icon={<Activity className="w-3.5 h-3.5 text-emerald-600" />} label="Sessions" value={visitsData.stats.total_sessions} />
                  <MiniStat icon={<Eye className="w-3.5 h-3.5 text-blue-600" />} label="Pages vues" value={visitsData.stats.total_pageviews} />
                  <MiniStat icon={<Clock className="w-3.5 h-3.5 text-amber-600" />} label="1ère visite" value={fmtShort(visitsData.stats.first_seen)} />
                  <MiniStat icon={<Clock className="w-3.5 h-3.5 text-purple-600" />} label="Dernière visite" value={fmtShort(visitsData.stats.last_seen)} />
                </div>

                {/* Devices / browsers / countries */}
                <div className="flex flex-wrap gap-3 text-xs">
                  {visitsData.stats.devices?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-rolex-green/55">Appareils :</span>
                      {visitsData.stats.devices.map((d, i) => {
                        const Icon = d === 'mobile' ? Smartphone : d === 'tablet' ? Tablet : Monitor;
                        return <Badge key={i} variant="outline" className="gap-1 capitalize"><Icon className="w-3 h-3" /> {d}</Badge>;
                      })}
                    </div>
                  )}
                  {visitsData.stats.browsers?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-rolex-green/55">Navigateurs :</span>
                      {visitsData.stats.browsers.map((b, i) => <Badge key={i} variant="outline">{b}</Badge>)}
                    </div>
                  )}
                  {visitsData.stats.countries?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-rolex-green/55">Pays :</span>
                      {visitsData.stats.countries.map((c, i) => <Badge key={i} variant="outline" className="gap-1"><Globe className="w-3 h-3" /> {c}</Badge>)}
                    </div>
                  )}
                  {visitsData.stats.ip_count > 0 && (
                    <Badge variant="outline" className="text-rolex-green/55">{visitsData.stats.ip_count} IP{visitsData.stats.ip_count > 1 ? 's' : ''} différente{visitsData.stats.ip_count > 1 ? 's' : ''}</Badge>
                  )}
                </div>

                {/* Top pages */}
                {visitsData.top_pages?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-rolex-green/55 font-bold mb-1.5">Pages les plus visitées</p>
                    <div className="space-y-1">
                      {visitsData.top_pages.slice(0, 10).map((p, i) => {
                        const maxViews = visitsData.top_pages[0].views;
                        const pct = Math.round((p.views / maxViews) * 100);
                        return (
                          <div key={i} className="bg-cream rounded-lg p-2 border border-gold/15">
                            <div className="flex justify-between text-xs">
                              <code className="font-mono text-rolex-green/85 truncate flex-1">{p.path}</code>
                              <span className="font-mono font-bold text-emerald-700 shrink-0 ml-2">{p.views} vues</span>
                            </div>
                            <div className="h-1 bg-stone-100 rounded-full overflow-hidden mt-1">
                              <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                {visitsData.timeline?.length > 0 && (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-rolex-green/55 font-bold mb-1.5">
                      Activité récente ({visitsData.timeline.length} dernières actions)
                    </p>
                    <div className="border border-gold/15 rounded-lg max-h-80 overflow-y-auto bg-cream/50 divide-y divide-gold/10">
                      {visitsData.timeline.map((ev, i) => (
                        <div key={i} className="p-2 hover:bg-cream/80 flex items-center gap-2 text-xs">
                          <Clock className="w-3 h-3 text-rolex-green/40 shrink-0" />
                          <span className="text-rolex-green/65 font-mono shrink-0 min-w-[110px]">{fmtShort(ev.ts)}</span>
                          <code className="font-mono text-rolex-green/85 truncate flex-1">{ev.path}</code>
                          {ev.referrer && <ExternalLink className="w-3 h-3 text-blue-500 shrink-0" title={ev.referrer} />}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {visitsData.timeline?.length === 0 && (
                  <div className="text-center py-8 text-rolex-green/55 text-sm">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Aucune visite enregistrée pour ce client.
                  </div>
                )}
              </div>
            ) : null}
            <DialogFooter>
              <Button onClick={() => setVisitsDialogOpen(false)} variant="outline" className="rounded-xl">Fermer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credit Dialog */}
        <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
          <DialogContent className="bg-white border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                <Coins className="w-5 h-5 text-gold-dark" /> {t('creditDebitPoints')}
              </DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4 py-2">
                {/* Client info */}
                <div className="rounded-xl bg-cream border border-gold/20 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-rolex-green/55 font-bold mb-0.5">Client</p>
                  <p className="font-bold text-rolex-green">{selectedUser.first_name} {selectedUser.last_name}</p>
                  <p className="text-xs text-rolex-green/65">{selectedUser.email}</p>
                  <p className="text-xs mt-1.5">Solde actuel : <span className="font-mono font-bold text-gold-dark">£{selectedUser.points_balance || 0}</span></p>
                </div>

                {/* Amount */}
                <div className="space-y-2">
                  <Label className="text-sm">Montant <span className="text-rolex-green/55 text-xs">(positif = créditer, négatif = débiter)</span></Label>
                  <Input
                    type="number"
                    value={creditForm.amount}
                    onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                    placeholder="ex: 100 ou -50"
                    className="rounded-xl font-mono text-lg h-12"
                    data-testid="credit-amount-input"
                  />
                </div>

                {/* Payment method selector — visible for any credit (positive or empty). Hidden for debits (negative) since refunds don't need a method. */}
                {(creditForm.amount === '' || parseInt(creditForm.amount || '0') >= 0) && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm">Méthode de paiement reçue</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { v: 'cash',          label: 'Espèces',     emoji: '💵' },
                          { v: 'bank_transfer', label: 'Virement',    emoji: '🏦' },
                          { v: 'card',          label: 'Carte CB',    emoji: '💳' },
                          { v: 'crypto',        label: 'Crypto',      emoji: '₿' },
                          { v: 'other',         label: 'Autre',       emoji: '📝' },
                          { v: 'offert',        label: 'Offert',      emoji: '🎁' },
                        ].map((opt) => {
                          const active = creditForm.payment_method === opt.v;
                          const isGift = opt.v === 'offert';
                          return (
                            <button
                              key={opt.v}
                              type="button"
                              onClick={() => setCreditForm({ ...creditForm, payment_method: opt.v })}
                              className={`p-2.5 rounded-xl border-2 text-center transition-all ${
                                active
                                  ? (isGift ? 'border-purple-400 bg-purple-50 shadow-md scale-[1.02]' : 'border-emerald-500 bg-emerald-50 shadow-md scale-[1.02]')
                                  : 'border-stone-200 bg-white hover:border-gold/40'
                              }`}
                              data-testid={`credit-method-${opt.v}`}
                            >
                              <p className="text-lg leading-none mb-1">{opt.emoji}</p>
                              <p className={`text-[10px] font-bold uppercase tracking-wider ${active ? (isGift ? 'text-purple-700' : 'text-emerald-700') : 'text-rolex-green/75'}`}>{opt.label}</p>
                            </button>
                          );
                        })}
                      </div>
                      {creditForm.payment_method === 'offert' ? (
                        <p className="text-[11px] text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-2">
                          🎁 <strong>Crédit offert</strong> : ne sera <strong>PAS comptabilisé</strong> dans le tableau de bord Ventes (cadeau / geste commercial).
                        </p>
                      ) : (
                        <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                          ✅ Sera <strong>comptabilisé dans Ventes</strong> comme vente manuelle (passerelle: manual).
                        </p>
                      )}
                    </div>

                    {/* Amount in GBP (only if not gift) */}
                    {creditForm.payment_method !== 'offert' && (
                      <div className="space-y-2">
                        <Label className="text-sm">Montant réellement encaissé en £ <span className="text-rolex-green/55 text-xs">(facultatif, défaut = montant crédité)</span></Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={creditForm.amount_gbp}
                          onChange={(e) => setCreditForm({ ...creditForm, amount_gbp: e.target.value })}
                          placeholder={`Par défaut : £${creditForm.amount || 0}`}
                          className="rounded-xl font-mono h-10"
                          data-testid="credit-amount-gbp-input"
                        />
                      </div>
                    )}
                  </>
                )}

                <div className="space-y-2">
                  <Label className="text-sm">Motif <span className="text-rolex-green/55 text-xs">(visible dans l'historique)</span></Label>
                  <Input
                    value={creditForm.reason}
                    onChange={(e) => setCreditForm({ ...creditForm, reason: e.target.value })}
                    placeholder="ex: Recharge en espèces / Compensation / Bonus parrainage"
                    className="rounded-xl"
                    data-testid="credit-reason-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreditDialogOpen(false)} className="rounded-xl">{t('cancel')}</Button>
              <Button className="bg-primary text-primary-foreground btn-press rounded-xl" onClick={handleCredit} data-testid="credit-confirm-btn">{t('confirm')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Confirmation Dialog (block / unblock / delete) */}
        <Dialog open={confirmDialog.open} onOpenChange={(o) => !o && setConfirmDialog({ open: false, action: null, user: null, typed: '' })}>
          <DialogContent className="bg-white border-border sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-2">
                {confirmDialog.action === 'delete' && <Trash2 className="w-5 h-5 text-red-600" />}
                {confirmDialog.action === 'block' && <Ban className="w-5 h-5 text-orange-600" />}
                {confirmDialog.action === 'unblock' && <ShieldCheck className="w-5 h-5 text-green-600" />}
                {confirmDialog.action === 'delete' && (t('confirmDelete') || 'Delete user?')}
                {confirmDialog.action === 'block' && (t('confirmBlock') || 'Block user?')}
                {confirmDialog.action === 'unblock' && (t('confirmUnblock') || 'Unblock user?')}
              </DialogTitle>
              <DialogDescription className="text-sm pt-2">
                {confirmDialog.user && (
                  <>
                    <span className="font-semibold">{confirmDialog.user.first_name} {confirmDialog.user.last_name}</span>
                    <br />
                    <span className="text-xs">{confirmDialog.user.email}</span>
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            {confirmDialog.action === 'delete' && (
              <div className="space-y-3">
                <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-800 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold mb-1">{t('deleteWarning') || 'This action is permanent and cannot be undone.'}</p>
                    <p>{t('deleteWarningDetail') || 'The user account, all their entries, transactions and notifications will be deleted.'}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">{t('typeDeleteToConfirm') || 'Type DELETE to confirm'}</Label>
                  <Input
                    value={confirmDialog.typed}
                    onChange={(e) => setConfirmDialog({ ...confirmDialog, typed: e.target.value.toUpperCase() })}
                    placeholder="DELETE"
                    className="rounded-xl font-mono uppercase"
                    data-testid="confirm-delete-input"
                  />
                </div>
              </div>
            )}
            {confirmDialog.action === 'block' && (
              <div className="rounded-xl bg-orange-50 border border-orange-200 p-3 text-xs text-orange-800 flex gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p>{t('blockExplain') || 'The user will not be able to log in until you unblock them. Their data is preserved.'}</p>
                </div>
              </div>
            )}
            {confirmDialog.action === 'unblock' && (
              <div className="rounded-xl bg-green-50 border border-green-200 p-3 text-xs text-green-800">
                {t('unblockExplain') || 'The user will be able to log in again immediately.'}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-xl" onClick={() => setConfirmDialog({ open: false, action: null, user: null, typed: '' })}>
                {t('cancel')}
              </Button>
              <Button
                onClick={performAction}
                disabled={actionLoading || (confirmDialog.action === 'delete' && confirmDialog.typed !== 'DELETE')}
                className={`rounded-xl ${
                  confirmDialog.action === 'delete'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : confirmDialog.action === 'block'
                      ? 'bg-orange-600 hover:bg-orange-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
                data-testid="confirm-action-btn"
              >
                {actionLoading
                  ? (t('processing') || 'Processing...')
                  : confirmDialog.action === 'delete'
                    ? (t('deletePermanently') || 'Delete permanently')
                    : confirmDialog.action === 'block'
                      ? (t('block') || 'Block')
                      : (t('unblock') || 'Unblock')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </main>
    </div>
  );
}

// ─── Helpers used by Visit History Dialog ───────────────────
function fmtShort(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function MiniStat({ icon, label, value }) {
  return (
    <div className="bg-cream rounded-lg p-2 border border-gold/15">
      <div className="flex items-center gap-1.5 mb-0.5">
        {icon}
        <p className="text-[9px] uppercase tracking-wider text-rolex-green/55 font-bold">{label}</p>
      </div>
      <p className="font-mono text-sm font-bold text-rolex-green truncate">{value || '—'}</p>
    </div>
  );
}
